import { useContext, useEffect, useState } from "react";
import CompanyService, { DataType } from "../../services/CompanyService";
import { FilterCollection } from "../../services/FilterService";
import InvoiceService from "../../services/InvoiceService";
import {
  formatCurrency,
  formatNumber,
  formatDateShort
} from "../../utils/FormatUtils";
import { parseDate, restTimezone, MapIfHasHours } from "../../utils/ParseUtils";
import { OptionalMap } from "../../utils/Utils";
import { InvoiceList } from "../client/invoice/entities/InvoiceListResponse";
import { TableColumn } from "../client/invoice/InvoiceTableItem";
import InvoiceTableProvider, {
  InvoiceTableContextValues
} from "../client/invoice/InvoiceTableProvider";
import InvoiceTableTotal from "../client/invoice/InvoiceTableTotal";
import AdvancedFilters, {
  AdvancedFiltersButton,
  TYPE_REPORT
} from "../shared/components/AdvancedFilters";
import Table, { TableHeaderWithFieldId } from "../shared/Table";
import { Link, useLocation } from "react-router-dom";
import TableContext from "../task/TableContext";
import ClientService from "../../services/ClientService";
import { TranslationService } from "../../services/TranslationService";
import MenuButton, { MenuButtonActions } from "../shared/components/MenuButton";
import { ActivityContentTableData } from "./ReportPayment";
import InvoiceDetail from "../client/invoice/InvoiceDetail";
import FloatingPanelService from "../shared/FloatingPanel";
import Dropdown from "../shared/components/Dropdown";
import SearchInput from "../shared/components/SearchInput";
import { CacheEntity, StorageService } from "../../services/StorageService";
import "../client/invoice/invoiceStyles.css";
import { handleErrorWithToast } from "../../utils/RequestUtils";

const ReportInvoice = () => {
  const tableHeaders = getTableHeaders();
  const { search } = useLocation();
  const urlParams = new URLSearchParams(search);
  const filterQS = urlParams.get("filter")
    ? [...urlParams.entries()]
        .map(
          (x, i) => "filter" + i.toString() + "=" + x[1].replace("filter0=", "")
        )
        .join("&")
    : "";
  const impid = urlParams.get("impid");
  const [quickFilter, setQuickFilter] = useState<number | undefined>(1);

  const invoiceFilters = [
    { value: 1, text: TranslationService.translate.PendingInvoices },
    { value: 0, text: TranslationService.translate.DueInvoices },
    { value: 4, text: TranslationService.translate.ClaimableInvoices },
    { value: 7, text: TranslationService.translate.PaidInvoices },
    { value: 2, text: TranslationService.translate.AllInvoices }
  ];

  const onChangeQuickFilter = (value: number) => {
    setQuickFilter(value);
  };

  return (
    <InvoiceTableProvider
      pagesize={50}
      extraFilters={filterQS}
      quickfilter={quickFilter?.toString() ?? ""}
      impid={impid}
    >
      <div className="container-fluid padding">
        <div className="d-flex justify-content-between">
          <h2>{TranslationService.translate.Invoices}</h2>
        </div>
        <div className="mh-100 p-0">
          <div className="genericHeader">
            <div className="searcherFilterHeader">
              <ExportButton />
              <AdvancedFiltersButton savedFiltersId={TYPE_REPORT["IO"]} />
              <div className="d-flex align-items-center">
                <p className="text-granite-gray me-3">
                  {TranslationService.translate.Status}:
                </p>
                <Dropdown
                  quickFilter
                  defaultValue={quickFilter}
                  onChange={onChangeQuickFilter}
                  items={invoiceFilters}
                />
              </div>
            </div>
            <Search />
          </div>
          <div className="pt-0">
            <AdvancedFilters
              page={FilterCollection.ReportInvoice}
              defaultValue={filterQS}
            />
          </div>
          <Table
            headers={tableHeaders}
            item={ReportInvoiceItem}
            stickyHeader={true}
            fillHeight={true}
          >
            <InvoiceTableTotal tableHeaders={tableHeaders} />
          </Table>
        </div>
      </div>
    </InvoiceTableProvider>
  );
};

export const ExportButton = ({
  ReportTypeId = 1
}: {
  ReportTypeId?: number;
}) => {
  const { getFilters, response } = useContext(
    TableContext
  ) as InvoiceTableContextValues;
  const exportInvoices = (reportId: number | null) => {
    InvoiceService.export(
      { ...getFilters(), reid: reportId },
      response!.itemCount
    );
  };
  const exportItems: MenuButtonActions = [
    {
      text: TranslationService.translate.Default,
      action: () => exportInvoices(null)
    },
    ...CompanyService.getReportExports()
      .filter((x) => x.Report === ReportTypeId)
      .map((x) => ({
        text: x.Name ?? TranslationService.translate.Default,
        action: () => exportInvoices(x.ReportExportID)
      }))
  ];
  return (
    <div className="actionButtonsHeader" style={{ marginTop: 1 }}>
      {CompanyService.canDo("export") && (
        <MenuButton
          text={TranslationService.translate.Export}
          actions={exportItems}
          extraClassName="select-intiza-outline"
          icon="fa-light fa-arrow-down-to-line"
          hideChevron
        />
      )}
    </div>
  );
};

export const ReportInvoiceItem = ({ data }: { data: InvoiceList.Item }) => {
  const tableHeaders = getTableHeaders();
  const clientFieldIds = ClientService.getClientHeaders().map((x) => x.fieldId);
  const specialFields = {
    "last-activity": (x: InvoiceList.Item) =>
      ActivityContentTableData({ Activity: x.LastLogMessage } as Parameters<
        typeof ActivityContentTableData
      >[0]),
    referenceNumber: (x: InvoiceList.Item) => (
      <td className="is-link">
        <span
          className="w-md pointer txt-blue"
          onClick={() =>
            FloatingPanelService.showPanel({
              children: <InvoiceDetail invoiceId={x.IOID} reload={() => {}} />,
              title: TranslationService.translate.Invoice,
              width: 750,
              height: 800
            })
          }
        >
          {data.referenceNumber}
        </span>
      </td>
    ),
    status: (item: InvoiceList.Item) => (
      <td>
        {CompanyService.getIoStatus().find(
          (x) => x.IOStatusID === item.IOStatusID
        )?.Value ?? CompanyService.getNormalIOStatusName()}
      </td>
    ),
    group: function GroupFieldImpl(item: InvoiceList.Item) {
      useEffect(() => {
        if (item.Group) {
          StorageService.setCache<string>(
            CacheEntity.GroupName,
            item.Group.GroupID.toString(),
            item.Group.Name
          );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);
      return <td>{item.Group ? item.Group.Name : ""}</td>;
    },
    download: () =>
      !data.HasFiles ? (
        <td></td>
      ) : (
        <td>
          <i
            className="fas fa-fw fa-download pointer"
            onClick={(e) => {
              e.stopPropagation();
              handleErrorWithToast(
                InvoiceService.downloadAllFilesFromInvoice(data.IOID)
              );
            }}
          />
        </td>
      )
  };

  return (
    <tr key={data.IOID}>
      {tableHeaders.map((header) =>
        clientFieldIds.includes(header.fieldId) ? (
          <ClientColumn key={header.fieldId} data={data} header={header} />
        ) : (
          <TableColumn
            key={header.fieldId}
            data={data}
            header={header}
            specialCases={specialFields}
          />
        )
      )}
    </tr>
  );
};

const ClientColumn = ({
  data,
  header
}: {
  data: InvoiceList.Item;
  header: TableHeaderWithFieldId;
}) => {
  const { currentLanguage } = TranslationService;
  const getValue = () => {
    if (header.key.startsWith("ioadditional.")) {
      return data.Person.additionals.find(
        (x) => x.Id.toString() === header.fieldId
      )?.Value;
    }
    return data.Person[header.fieldId as keyof InvoiceList.Item["Person"]];
  };
  const value = getValue();
  switch (header.fieldId) {
    case "Name":
      return (
        <td className="is-link th-truncate">
          <span className="w-md">
            <Link to={`/${currentLanguage}/client/details?id=${data.personId}`}>
              {data.Person.Name}
            </Link>
          </span>
        </td>
      );
  }
  if (value === undefined || value === null || value === "") {
    return <td></td>;
  }
  switch (header.type) {
    case DataType.Currency:
      return (
        <td className="text-end th-truncate">
          {formatCurrency(parseFloat(value), data.CurrencyID)}
        </td>
      );
    case DataType.Number:
      return <td className="text-end th-truncate">{formatNumber(value)}</td>;
    case DataType.Date:
      return (
        <td className="th-truncate">
          {OptionalMap(parseDate(value), (x) =>
            formatDateShort(MapIfHasHours(x, restTimezone))
          )}
        </td>
      );
    case DataType.List: {
      const additionalId = data.Person.additionals.find(
        (x) => x.Id.toString() === header.fieldId
      )?.Id;
      const additionalDefinition =
        CompanyService.getAdditionalDefinitions().find(
          (x) =>
            x.AdditionalDefinitionID.toString() === additionalId?.toString()
        );
      const definition = additionalDefinition?.AdditionalDefinitionItems.find(
        (x) => x.AdditionalDefinitionItemID.toString() === value
      );
      return <td className="th-truncate">{definition?.Value}</td>;
    }
    default:
      return <td className="th-truncate">{value}</td>;
  }
};

function getTableHeaders() {
  const tableHeaders = InvoiceService.getTableHeaders();
  const idxStatus = tableHeaders.findIndex((x) => x.fieldId === "status");
  if (idxStatus >= 0) {
    const status = { ...tableHeaders[idxStatus] };
    status.numeric = false;
    tableHeaders.splice(idxStatus, 1, status);
  }

  const clientHeaders = ClientService.getClientHeaders();

  const sortedClientHeaders = clientHeaders;

  // Agrandar anchos de las columnas de clientes
  sortedClientHeaders.forEach((header) => {
    if (header.fieldId === "Name") {
      header.className =
        (header.className || "").replace(/long-text/g, "").trim() +
        " long-text w-300px th-truncate";
    } else if (!header.className || header.className.trim() === "") {
      header.className = "w-200px th-truncate";
    } else {
      if (!header.className.includes("w-")) {
        header.className = header.className.trim() + " w-200px";
      }
      if (!header.className.includes("th-truncate")) {
        header.className = header.className.trim() + " th-truncate";
      }
    }
  });

  if (sortedClientHeaders.find((x) => x.fieldId === "Name") === undefined) {
    sortedClientHeaders.unshift(
      new TableHeaderWithFieldId(
        "client",
        TranslationService.translate.Client,
        false,
        true,
        "Name",
        DataType.Client,
        "w-300px th-truncate"
      )
    );
  }

  tableHeaders.unshift(...sortedClientHeaders);

  tableHeaders.push(
    new TableHeaderWithFieldId(
      "last-activity",
      TranslationService.translate.LastActivity,
      false,
      false,
      "last-activity",
      DataType.ActivityType,
      "th-min-200 th-max-600 th-truncate"
    )
  );

  tableHeaders.push({
    fieldId: "download",
    key: "donwload",
    className: "th-w-40 th-center",
    label: () => <></>,
    numeric: false,
    sorteable: false,
    tooltip: "",
    type: DataType.Download
  });

  return tableHeaders;
}

const Search = () => {
  const { applySearch } = useContext(TableContext);
  return (
    <div className="searcherFilterHeader" style={{ height: 40 }}>
      <SearchInput onSearch={applySearch} autosearch={false} />
    </div>
  );
};

export default ReportInvoice;
